"use client";

import * as RadixSelect from "@radix-ui/react-select";
import { cva } from "class-variance-authority";
import type { SelectHTMLAttributes } from "react";
import React from "react";
import { cn } from "../../helpers/styles";
import { Icon } from "../Icon/Icon";

export type Props = {
	variant?: "primary" | "secondary";
	onChange: RadixSelect.SelectProps["onValueChange"];
	placeholder?: string;
} & Pick<RadixSelect.SelectProps, "defaultValue" | "value"> &
	Omit<SelectHTMLAttributes<HTMLButtonElement>, "aria-placeholder">;

export const Select = React.forwardRef<HTMLButtonElement, Props>(
	(
		{
			className,
			variant = "primary",
			onChange,
			value,
			defaultValue = "",
			children,
			placeholder = "",
			...props
		},
		ref,
	) => (
		<RadixSelect.Root onValueChange={onChange} value={value} defaultValue={defaultValue}>
			<RadixSelect.Trigger
				aria-placeholder={placeholder}
				className={cn(
					"transition-all",
					// reset
					"appearance-none",
					selectVariants({ variant }),
					// placeholder text
					"text-left text-md font-medium data-[placeholder]:py-2.5 data-[placeholder]:pl-3.5 data-[placeholder]:pr-11 data-[placeholder]:text-md data-[placeholder]:font-normal data-[placeholder]:text-gray-500",
					className,
				)}
				{...props}
				ref={ref}
			>
				<RadixSelect.Value placeholder={placeholder} />
			</RadixSelect.Trigger>

			<RadixSelect.Portal>
				<RadixSelect.Content className="z-50 max-h-12 rounded-xs bg-white shadow">
					<RadixSelect.ScrollUpButton />
					<RadixSelect.Viewport className="px-1.5">{children}</RadixSelect.Viewport>
					<RadixSelect.ScrollDownButton />
					<RadixSelect.Arrow />
				</RadixSelect.Content>
			</RadixSelect.Portal>
		</RadixSelect.Root>
	),
);

Select.displayName = "Select";

export const selectVariants = cva(
	"min-h-11 font-sans focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-0",
	{
		variants: {
			variant: {
				primary: [
					// shape
					"w-full rounded-xs border border-gray-300 bg-white px-lg py-md pr-14 shadow-sm",
					// font
					"text-sm font-medium",
					// caret
					"bg-no-repeat [background-image:--chevron] [background-position:calc(100%-1rem)] [background-size:1.5rem]",
					// disabled
					"disabled:cursor-not-allowed disabled:opacity-50",
				],
				secondary: [
					// shape
					"focus:ring-accent-level-2 relative w-full rounded-xs bg-gray-50 px-lg py-md pr-14 shadow-1 hover:border-gray-300 hover:bg-gray-50 hover:shadow-2",
					// font
					"text-center text-sm font-semibold text-gray-700",
					// caret
					"bg-no-repeat [background-image:--icon-chevron-down] [background-position:calc(100%-26px)] [background-size:1.5rem]",
					// disabled
					"disabled:pointer-events-none disabled:select-none disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none",
				],
			},
		},
		defaultVariants: {
			variant: "primary",
		},
	},
);

export const Option = React.forwardRef<HTMLDivElement, RadixSelect.SelectItemProps>(
	({ children, className, ...props }, forwardedRef) => (
		<RadixSelect.Item
			className={cn(
				"group/item relative flex cursor-pointer select-none flex-row items-center p-px",
				className,
			)}
			{...props}
			ref={forwardedRef}
		>
			<div className="w-full py-2.5 pl-2 pr-10 text-md font-medium group-hover/item:bg-gray-50 group-data-[state=checked]/item:bg-gray-50">
				<RadixSelect.ItemText>{children}</RadixSelect.ItemText>
				<RadixSelect.ItemIndicator>
					<Icon icon="check" className="absolute right-2.5 top-0 size-5 h-full bg-brand-600" />
				</RadixSelect.ItemIndicator>
			</div>
		</RadixSelect.Item>
	),
);

Option.displayName = "Option";
